// CONTRACT:C2-RENDER-ENGINE.1.0
/** Backend adapter seam — the thin internal boundary that hides the renderer.
 *
 *  The contract's load-bearing rule: the backing renderer (MapLibre v5 +
 *  deck.gl v9 / WebGL2 today, WebGPU later) is HIDDEN behind RenderEngine, and
 *  swapping it MUST NOT change any consumer. We get that here: engine.ts talks
 *  only to `RenderBackend`; the deck/MapLibre calls live behind it. Tests inject
 *  a fake backend so the pure orchestration is exercisable without WebGL.
 */

import type { LayerSpec, ViewState } from "../../contracts-ts/src/index.ts";

/** What the engine asks a backend to do. Implementations: WebGL2 (deck+MapLibre), WebGPU (future). */
export interface RenderBackend {
  /** Push the camera to the underlying map. */
  setView(view: ViewState): void;
  /** Create the GPU layer for a spec. */
  createLayer(spec: LayerSpec): void;
  /** Patch ONLY the changed attributes; `changedTriggers` flip deck updateTriggers.
   *  When `rebuild` is true the layer is recreated; otherwise it is patched in place. */
  patchLayer(
    spec: LayerSpec,
    change: { changedProps: string[]; changedTriggers: string[]; rebuild: boolean },
  ): void;
  /** Drop the GPU layer. */
  destroyLayer(id: string): void;
  /** Write the coalesced buffer for this frame (ONE write per frame). */
  writeFrame(layerData: ReadonlyMap<string, unknown[]>): void;
  /** Synchronous hit-test passthrough. */
  pick(screen: { x: number; y: number }): { id: string } | null;
  /** Measure the last frame's GPU work (ms) for budget evaluation. */
  lastFrameMs(): number;
  /** Approximate GPU bytes allocated, for alloc-cap chunking decisions. */
  allocatedBytes(): number;
  dispose(): void;
}

/** A no-op backend used when no GPU surface is attached yet (SSR / headless construct).
 *  Real rendering requires the WebGL2 backend; this keeps the engine constructible
 *  and the data plane (store/time/layers) fully usable without a canvas. */
export class NullBackend implements RenderBackend {
  setView(): void {}
  createLayer(): void {}
  patchLayer(): void {}
  destroyLayer(): void {}
  writeFrame(): void {}
  pick(): { id: string } | null {
    return null;
  }
  lastFrameMs(): number {
    return 0;
  }
  allocatedBytes(): number {
    return 0;
  }
  dispose(): void {}
}
